<script setup lang="ts">
import { useFormValidation } from "../../../../../composables/useFormValidation";
import { required, Amount } from "../../../../../utils/validation";
import TextInput from "../../../../../components/ui/form/TextInput.vue";
import NumberInput from "../../../../../components/ui/form/NumberInput.vue";
import CheckInput from "../../../../../components/ui/form/CheckInput.vue";
import SearchSelect from "../../../../../components/ui/form/SearchSelect.vue";
import { api } from "../../../../../api/Api";
import Toast from "../../../../../services/Toast";
import { Permission } from "../../../../../types/auth";
import { isEmpty } from "../../../../../utils/CommonFunctions";
import MessageBoxService from "../../../../../services/MessageBox";
import { ref, onMounted } from "vue";
import ModalFormTemplate from "../../../../../components/ui/Templates/ModalFormTemplate.vue";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  unit_of_measures?: any | [];
  variants?: any | [];
  permissions?: Permission[];
  listCreateName?: string;
  item?: any | null;
}>();

const emit = defineEmits<{
  (e: "save", data: any): void;
  (e: "close"): void;
}>();


const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  resetForm,
  setFormData,
} = useFormValidation({
  items_id: props.data?.items_id || props.item?.id || "",
  code: props.data?.code || "",
  unit_of_measures_id: props.data?.unit_of_measures_id || "",
  unit_of_measure: props.data?.unit_of_measure || "",
  item_variants_id: props.data?.item_variants_id || "",
  all_variants: props.data?.all_variants || false,
  item: props.data?.item || props.item?.name || "",
  price_list: props.data?.price_list || "",
  price_lists_id: props.data?.price_lists_id || "",
  inventory_qty: props.data?.inventory_qty || 1,
  selling_price: props.data?.selling_price || 0,
  packing_price: props.data?.packing_price || 0,
  delivery_charges: props.data?.delivery_charges || 0,
  free_delivery_amount: props.data?.free_delivery_amount || 0,
  points: props.data?.points || 0,
  always_charge_packing: props.data?.always_charge_packing || false,
  always_charge_delivery: props.data?.always_charge_delivery || false,
  active: props.data?.active || true,
  uid: props.data?.uid || "",
});

setFieldRules("item", [required("Item is required")]);
setFieldRules("price_lists_id", [required("Price List is required")]);
setFieldRules("inventory_qty", [Amount("Price List is required")]);
setFieldRules("selling_price", [Amount("Price is required")]);
setFieldRules("item_variants_id", props.item?.has_variants && !fields.value.all_variants.value ? [required("Item Variant is required")] : []);

const handleSubmit = async (close: boolean, newRecord: boolean) => {
  if (validateForm()) {
    if (isEmpty(formData.value.code)) {
      const confirmed = await MessageBoxService.confirm({
        title: "Unique Code missing",
        message:
          "There is not code provided <br /> while code is optional, if a code is provded you can use it as a barcode on the POS and other interfaces.<br /> Are you sure you want to proceed without a code?",
        confirmText: "I will provide a Code",
        cancelText: "Proceed wihtout Code",
        type: "warning",
      });
      if (confirmed) return;
    }
    const action = formData.value.uid != "" ? "update" : "create";
    const response = await api.post(
      (props.endPoint.slice(-1) == "/" || props.endPoint.slice(-1) == "-"
        ? props.endPoint
        : props.endPoint + "/") + action,
      formData.value
    );
    if (response.success == true) {
      Toast.show("saved successfully");
      emit("save", response.data);
      if (close) {
        emit("close");
      } else if (newRecord) {
        resetForm();
      } else {
        setFormData(response.data);
      }
    }
  }
};

const toggleAllVariants = () => {
  if (fields.value.all_variants.value) {
    fields.value.item_variants_id.value = "";
    setFieldRules("item_variants_id", []);
  } else {
    setFieldRules("item_variants_id", [required("Item Variant is required")]);
  }
};
const isReadOnly = ref(true);
onMounted(() => {
  isReadOnly.value = props.mode == 'view'
  if (props.mode == 'create') {
      resetForm()
    } else if (props.data) {
      setFormData(props.data)
      console.log("formData", formData.value);
    }
})

const handleSelectUnitOfMeasure = (value: any) => {
  console.log("handleSelectUnitOfMeasure", value);
  if (value && value.code && props.mode == 'create') {
    fields.value.code.value = value.code;
  } 
};
</script>
<template>
  <ModalFormTemplate
    :data="data"
    :mode="mode"
    :title="title"
    :endPoint="endPoint"
    :perm="permissions"
    :listCreateName="listCreateName"
    width="45rem"
    :tabList="[]"
    :formData="formData"
    @save="handleSubmit"
    @close="$emit('close')"
  >
    <template #form>
      <div id="" class="grid md:grid-cols-2 gap-6">
        <TextInput
          v-model="fields.item.value"
          label="Item"
          :readonly="true"
          :error="fields.item.touched ? fields.item.error : null"
          @blur="fields.item.touched = true"
        />
        <TextInput
          v-model="fields.code.value"
          label="Code"
          :readonly="isReadOnly"
          :error="fields.code.touched ? fields.code.error : null"
          @blur="fields.code.touched = true"
        />
        <SearchSelect
          v-model="fields.item_variants_id.value"
          :options="item?.item_variants || []"
          v-if="item?.has_variants"
          label="Variant"
          :readonly="isReadOnly"
          :error="
            fields.item_variants_id.touched ? fields.item_variants_id.error : null
          "
          @blur="fields.item_variants_id.touched = true"
        />
        <CheckInput
          v-model="fields.all_variants.value"
          :readonly="isReadOnly"
          @change="toggleAllVariants"
          v-if="item?.has_variants && mode == 'create'"
          label="All Variants"
          :error="fields.all_variants.touched ? fields.all_variants.error : null"
          @blur="fields.all_variants.touched = true"
        />
      </div>
      <div id="" class="grid md:grid-cols-3 gap-6">
        <TextInput
          v-model="fields.price_list.value"
          label="Price List"
          :readonly="true"
          :error="fields.price_list.touched ? fields.price_list.error : null"
          v-if="mode == 'view'"
          @blur="fields.price_list.touched = true"
        />
        <SearchSelect
          v-else
          v-model="fields.price_lists_id.value"
          end-point="finance/master-data/price-lists"
          label="Price List"
          :readonly="isReadOnly"
          :error="
            fields.price_lists_id.touched ? fields.price_lists_id.error : null
          "
          @blur="fields.price_lists_id.touched = true"
        />

        <TextInput
          v-model="fields.unit_of_measure.value"
          label="Unit Of Measure"
          :readonly="true"
          :error="
            fields.unit_of_measure.touched ? fields.unit_of_measure.error : null
          "
          v-if="mode == 'view'"
          @blur="fields.unit_of_measure.touched = true"
        />
        <SearchSelect
          v-else
          v-model="fields.unit_of_measures_id.value"
          :options="unit_of_measures"
          @select="handleSelectUnitOfMeasure"
          label="Unit Of Measure"
          :readonly="isReadOnly"
          end-point="inventory/item-management/unit-of-measures"
          :error="
            fields.unit_of_measures_id.touched
              ? fields.unit_of_measures_id.error
              : null
          "
          @blur="fields.unit_of_measures_id.touched = true"
          :allow-new-record="true"
        />

        <NumberInput
          v-model="fields.inventory_qty.value"
          :readonly="isReadOnly"
          label="inventory_qty"
          :min="1"
          :error="
            fields.inventory_qty.touched ? fields.inventory_qty.error : null
          "
          @blur="fields.inventory_qty.touched = true"
        />

        <NumberInput
          v-model="fields.selling_price.value"
          :readonly="isReadOnly"
          label="Selling Price"
          :min="0"
          :error="
            fields.selling_price.touched ? fields.selling_price.error : null
          "
          @blur="fields.selling_price.touched = true"
        />

        <NumberInput
          v-model="fields.packing_price.value"
          :readonly="isReadOnly"
          label="Packing Price"
          :min="0"
          :error="
            fields.packing_price.touched ? fields.packing_price.error : null
          "
          @blur="fields.packing_price.touched = true"
        />

        <NumberInput
          v-model="fields.delivery_charges.value"
          :readonly="isReadOnly"
          label="Delivery Charge"
          :min="0"
          :error="
            fields.delivery_charges.touched
              ? fields.delivery_charges.error
              : null
          "
          @blur="fields.delivery_charges.touched = true"
        />

        <NumberInput
          v-model="fields.free_delivery_amount.value"
          :readonly="isReadOnly"
          label="Free Delivery Amount"
          :min="0"
          :error="
            fields.free_delivery_amount.touched
              ? fields.free_delivery_amount.error
              : null
          "
          @blur="fields.free_delivery_amount.touched = true"
        />

        <NumberInput
          v-model="fields.points.value"
          :readonly="isReadOnly"
          label="Points"
          :min="0"
          :error="fields.points.touched ? fields.points.error : null"
          @blur="fields.points.touched = true"
        />

        <CheckInput
          v-model="fields.always_charge_packing.value"
          :readonly="isReadOnly"
          label="Always Charge Packing"
          :error="
            fields.always_charge_packing.touched
              ? fields.always_charge_packing.error
              : null
          "
          @blur="fields.always_charge_packing.touched = true"
        />

        <CheckInput
          v-model="fields.always_charge_delivery.value"
          :readonly="isReadOnly"
          label="Always Charge Delivery"
          :error="
            fields.always_charge_delivery.touched
              ? fields.always_charge_delivery.error
              : null
          "
          @blur="fields.always_charge_delivery.touched = true"
        />

        <CheckInput
          v-model="fields.active.value"
          :readonly="isReadOnly"
          label="Active"
          :error="fields.active.touched ? fields.active.error : null"
          @blur="fields.active.touched = true"
        />
      </div>
    </template>
  </ModalFormTemplate>
</template>
